var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useOkapiKy } from "@folio/stripes/core";
import { PublisherLegalFormEnum, } from "../types/Api";
import { createPublisherFormSchema, } from "../utils/validationUtils";
import { fetchRegions } from "../api/regionService";
export var usePublisherForm = function (initialValues) {
    var formatMessage = useIntl().formatMessage;
    var ky = useOkapiKy();
    var regionsData = useQuery(["regions"], function () {
        return fetchRegions(ky);
    }).data;
    var publisherFormSchema = createPublisherFormSchema(formatMessage);
    var _a = useState(!!initialValues.mailingAddress), showMailingAddress = _a[0], setShowMailingAddress = _a[1];
    var defaultValues = useMemo(function () {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u;
        return ({
            owner: initialValues.owner,
            name: (_a = initialValues.name) !== null && _a !== void 0 ? _a : "",
            nameSuffix: (_b = initialValues.nameSuffix) !== null && _b !== void 0 ? _b : "",
            legalForm: (_c = initialValues.legalForm) !== null && _c !== void 0 ? _c : PublisherLegalFormEnum.LEGAL,
            companyNumber: (_d = initialValues.companyNumber) !== null && _d !== void 0 ? _d : "",
            taxNumber: (_e = initialValues.taxNumber) !== null && _e !== void 0 ? _e : "",
            birthDate: (_f = initialValues.birthDate) !== null && _f !== void 0 ? _f : null,
            mainAddress: {
                street: (_h = (_g = initialValues.mainAddress) === null || _g === void 0 ? void 0 : _g.street) !== null && _h !== void 0 ? _h : "",
                postalCode: (_k = (_j = initialValues.mainAddress) === null || _j === void 0 ? void 0 : _j.postalCode) !== null && _k !== void 0 ? _k : "",
                municipality: (_m = (_l = initialValues.mainAddress) === null || _l === void 0 ? void 0 : _l.municipality) !== null && _m !== void 0 ? _m : "",
                regionId: (_p = (_o = initialValues.mainAddress) === null || _o === void 0 ? void 0 : _o.regionId) !== null && _p !== void 0 ? _p : "",
            },
            mailingAddress: initialValues.mailingAddress
                ? {
                    street: (_q = initialValues.mailingAddress.street) !== null && _q !== void 0 ? _q : "",
                    postalCode: (_r = initialValues.mailingAddress.postalCode) !== null && _r !== void 0 ? _r : "",
                    municipality: (_s = initialValues.mailingAddress.municipality) !== null && _s !== void 0 ? _s : "",
                    regionId: (_t = initialValues.mailingAddress.regionId) !== null && _t !== void 0 ? _t : "",
                }
                : null,
            contacts: ((_u = initialValues.contacts) !== null && _u !== void 0 ? _u : []).map(function (contact) {
                var _a, _b, _c, _d;
                return ({
                    id: (_a = contact.id) !== null && _a !== void 0 ? _a : null,
                    type: contact.type,
                    value: (_b = contact.value) !== null && _b !== void 0 ? _b : "",
                    info: (_c = contact.info) !== null && _c !== void 0 ? _c : "",
                    isPrimary: (_d = contact.isPrimary) !== null && _d !== void 0 ? _d : false,
                });
            }),
        });
    }, [initialValues]);
    var publisherForm = useForm({
        resolver: zodResolver(publisherFormSchema),
        defaultValues: defaultValues,
        mode: "onSubmit",
        shouldFocusError: false,
    });
    var reset = publisherForm.reset, watch = publisherForm.watch;
    var legalFormValue = watch("legalForm");
    useEffect(function () {
        reset(defaultValues);
    }, [defaultValues, reset]);
    var prepareSubmitData = function (data) {
        var _a;
        var mainAddress = data.mainAddress
            ? {
                street: data.mainAddress.street,
                postalCode: data.mainAddress.postalCode,
                municipality: data.mainAddress.municipality,
                regionId: data.mainAddress.regionId,
            }
            : undefined;
        var mailingAddress = data.mailingAddress && showMailingAddress
            ? {
                street: data.mailingAddress.street,
                postalCode: data.mailingAddress.postalCode,
                municipality: data.mailingAddress.municipality,
                regionId: data.mailingAddress.regionId,
            }
            : undefined;
        var contacts = ((_a = data.contacts) !== null && _a !== void 0 ? _a : [])
            .filter(function (contact) { return contact.value.trim() !== ""; })
            .map(function (contact) {
            var _a, _b;
            return ({
                id: (_a = contact.id) !== null && _a !== void 0 ? _a : undefined,
                type: contact.type,
                value: contact.value,
                info: contact.info || undefined,
                isPrimary: (_b = contact.isPrimary) !== null && _b !== void 0 ? _b : false,
            });
        });
        return __assign(__assign({}, initialValues), { owner: data.owner, name: data.name, nameSuffix: data.nameSuffix || undefined, legalForm: data.legalForm, companyNumber: data.companyNumber || undefined, taxNumber: data.taxNumber || undefined, birthDate: data.birthDate || undefined, mainAddress: mainAddress, mailingAddress: mailingAddress, contacts: contacts || [] });
    };
    return {
        publisherForm: publisherForm,
        regionsData: regionsData,
        legalFormValue: legalFormValue,
        showMailingAddress: showMailingAddress,
        setShowMailingAddress: setShowMailingAddress,
        prepareSubmitData: prepareSubmitData,
    };
};
